import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

import { SupportApiService } from '../core/support-api.service';
import { FooterComponent } from '../shell/footer.component';

/** Spec, §Unauthenticated intake form. Zero auth required — the SWA route rule
 * carves this out (Task 5). Posts to the PUBLIC POST /intake endpoint: the server
 * resolves the triage queue and forces auto-priority high
 * (create_ticket's intake_channel='unauthenticated' rule), so this form never
 * offers a priority field and never chooses a queue. The honeypot short-circuits
 * client-side here AND is re-checked at the API layer alongside a per-IP rate
 * limit (routers/intake.py) — defense in depth, both ends. */
@Component({
  selector: 'app-intake-form',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbTooltip, FooterComponent],
  template: `
    <div class="intake-bg">
      <div class="intake-shell">
        <header class="intake-brand">
          <img class="intake-brand__logo intake-brand__logo--light" src="assets/logos/Recon/Blue-Titan-Emblem-Logo-Recon@4x.png" alt="" />
          <img class="intake-brand__logo intake-brand__logo--dark" src="assets/logos/White/Blue-Titan-Emblem-Logo-White@4x.png" alt="" />
          <span class="bt-wordmark intake-brand__name" data-testid="bt-wordmark">BlueTitan&nbsp;Support</span>
        </header>

        <div class="bt-card intake-card">
          @if (!confirmationReference) {
            <div class="intake-head">
              <h1 class="intake-title">Can't log in?</h1>
              <p class="intake-intro">
                Login issues are almost always something BlueTitan needs to fix on its side — the
                more detail you give us, especially the exact error text and any code like
                <strong>AADSTS500206</strong>, the faster we can resolve it. Every submission here is
                treated as <strong>high priority</strong> automatically — there is nothing for you to set.
              </p>
            </div>

            <form class="intake-form" (submit)="$event.preventDefault(); submit()">
              <div class="bt-field">
                <label class="bt-label" for="in-email" ngbTooltip="The address you tried to sign in with — we'll reply here.">Email you tried to log in with</label>
                <input id="in-email" class="bt-input" type="email" [(ngModel)]="email" name="email" data-testid="intake-email" required placeholder="you@agency.gov" />
              </div>

              <div class="bt-field">
                <label class="bt-label" for="in-org" ngbTooltip="Your agency or company name, as best you know it.">Organization <span class="opt">(as best you know it)</span></label>
                <input id="in-org" class="bt-input" [(ngModel)]="organization" name="organization" data-testid="intake-organization" placeholder="Example Police Department" />
              </div>

              <div class="bt-field">
                <label class="bt-label" for="in-err" ngbTooltip="Paste the full error message verbatim, including any AADSTS code — that code alone often tells us exactly what's wrong.">Paste the full error text <span class="opt">(including any AADSTS code)</span></label>
                <textarea id="in-err" class="bt-textarea" [(ngModel)]="errorText" name="errorText" data-testid="intake-error-text" required placeholder="e.g. AADSTS500206: The account is not allowed to sign in…"></textarea>
              </div>

              <div class="bt-field">
                <label class="bt-label" for="in-before" ngbTooltip="Helps us tell a brand-new account problem from something that broke.">Have you ever logged in successfully before?</label>
                <select id="in-before" class="bt-select" [(ngModel)]="everLoggedInBefore" name="everLoggedInBefore" data-testid="intake-ever-logged-in">
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>

              <input type="text" name="website" data-testid="intake-honeypot" aria-hidden="true"
                     tabindex="-1" autocomplete="off" class="intake-honeypot" [(ngModel)]="honeypot" />

              <button type="submit" class="bt-btn bt-btn--primary intake-submit" data-testid="intake-submit"
                      ngbTooltip="Send this to BlueTitan support — marked high priority automatically.">
                Send to BlueTitan Support
              </button>
            </form>
          } @else {
            <div class="intake-confirmation" data-testid="intake-confirmation">
              <span class="intake-confirmation__check">✓</span>
              <h1 class="intake-title">Ticket created</h1>
              <p>Your reference number is</p>
              <p class="intake-confirmation__ref">{{ confirmationReference }}</p>
              <p class="intake-confirmation__hint">Please keep this reference for any follow-up. Our team will reply to the email you provided.</p>
            </div>
          }
        </div>
      </div>
      <app-footer></app-footer>
    </div>
  `,
  styles: [`
    .intake-bg {
      min-height: 100vh; display: flex; flex-direction: column; align-items: center;
      padding: 2.5rem 1.25rem 1.5rem;
      background:
        radial-gradient(1100px 480px at 50% -8%, color-mix(in srgb, var(--bt-brand-intercept) 16%, transparent), transparent 70%),
        var(--bt-bg-body);
    }
    .intake-shell { width: 100%; max-width: 40rem; flex: 1; }
    .intake-brand { display: flex; align-items: center; justify-content: center; gap: 0.6rem; margin-bottom: 1.5rem; }
    .intake-brand__logo { height: 40px; width: auto; }
    .intake-brand__logo--dark { display: none; }
    .intake-brand__name { font-size: 1.5rem; font-weight: 700; color: var(--bt-text-primary); }

    .intake-card { padding: 2rem 2.25rem; }
    .intake-head { margin-bottom: 1.5rem; }
    .intake-title { font-family: 'Montserrat', sans-serif; font-size: 1.6rem; font-weight: 700; margin: 0 0 0.6rem; color: var(--bt-text-primary); }
    .intake-intro { margin: 0; color: var(--bt-text-secondary); line-height: 1.6; }

    .intake-form { display: flex; flex-direction: column; gap: 1.1rem; }
    .bt-label .opt { font-weight: 400; color: var(--bt-text-muted); }
    .intake-honeypot { display: none; }
    .intake-submit { align-self: flex-start; margin-top: 0.25rem; padding: 0.6rem 1.4rem; font-size: 15px; }

    .intake-confirmation { text-align: center; padding: 1.5rem 0.5rem; }
    .intake-confirmation__check {
      display: inline-flex; align-items: center; justify-content: center; width: 56px; height: 56px; margin-bottom: 1rem;
      border-radius: 50%; font-size: 28px; font-weight: 700;
      background: color-mix(in srgb, var(--bt-status-success) 15%, transparent); color: var(--bt-status-success);
    }
    .intake-confirmation p { margin: 0.35rem 0; color: var(--bt-text-secondary); }
    .intake-confirmation__ref {
      font-family: 'Roboto Mono', ui-monospace, monospace; font-size: 1.7rem; font-weight: 700;
      color: var(--bt-brand-intercept) !important; letter-spacing: 0.02em;
    }
    .intake-confirmation__hint { font-size: 13px; max-width: 26rem; margin: 0.75rem auto 0 !important; }

    :host-context(body.theme-dark) .intake-brand__logo--light { display: none; }
    :host-context(body.theme-dark) .intake-brand__logo--dark { display: block; }

    @media (max-width: 560px) { .intake-card { padding: 1.5rem 1.25rem; } }
  `],
})
export class IntakeFormComponent {
  private api = inject(SupportApiService);

  email = '';
  organization = '';
  errorText = '';
  everLoggedInBefore: 'yes' | 'no' = 'no';
  honeypot = '';
  confirmationReference: string | null = null;

  submit(): void {
    if (this.honeypot) return; // silently drop — a bot filled the hidden field
    const capturedAt = new Date().toISOString(); // client-side, sent as-is, never reformatted
    this.api.submitIntake({
      email: this.email,
      organization: this.organization,
      error_text: this.errorText,
      ever_logged_in_before: this.everLoggedInBefore === 'yes',
      reported_at_client: capturedAt,
      website: this.honeypot,
    }).subscribe((result) => {
      this.confirmationReference = result.reference;
    });
  }
}
